import { eUserType } from '@app/shared/enum/user-type';

export type SubscriptionStatus = 'active' | 'expiringSoon' | 'expired';
export type AutoRenewIneligibleReason = 'noCard' | 'expired' | 'noSubscription';

export interface SubscriptionInfo {
  status: SubscriptionStatus;
  expiryDate: string | null;
  planId: string | null;
  canRenew: boolean;
  planName?: string | null;
  planInterval?: string | null;
  daysRemaining?: number | null;
  billingDate?: string | null;
  /** Auto-Renew setting; absent when the backend does not send it, in which case the card shows nothing new. */
  autoRenew?: boolean;
  autoRenewEligible?: boolean;
  autoRenewIneligibleReason?: AutoRenewIneligibleReason | null;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Only the owner of the subscription may change Auto-Renew; Financial Admins and every other role see it read-only. */
export function canChangeAutoRenew(userType: number): boolean {
  return userType === eUserType.SHIPPER || userType === eUserType.CARRIER || userType === eUserType.OWNER_OPERATOR;
}

/** How every subscription date is written on the screens, for example "Nov 16, 2026". */
export const SUBSCRIPTION_DATE_FORMAT = 'MMM d, yyyy';

export const PREMIUM_PLAN_LABEL = 'Premium Plan';
export const STANDARD_PLAN_LABEL = 'Standard Plan';

/**
 * Annual plans are shown as Premium and monthly plans as Standard. The backend interval wins; otherwise
 * `isAnnual` (from comparing the plan id with the annual plan) decides, and null means unknown.
 */
export function getPlanLabel(info: SubscriptionInfo | null, isAnnual: boolean | null = null): string | null {
  const interval = info && info.planInterval;
  if (interval === 'year') {
    return PREMIUM_PLAN_LABEL;
  }
  if (interval === 'month') {
    return STANDARD_PLAN_LABEL;
  }
  if (isAnnual === null || isAnnual === undefined) {
    return null;
  }
  return isAnnual ? PREMIUM_PLAN_LABEL : STANDARD_PLAN_LABEL;
}

/**
 * The plan the user is on now. The `subscription` block is kept current, while the legacy `subscriptionPlan`
 * record can still hold the previous plan after an upgrade, so it is only the fallback.
 */
export function getCurrentPlanId(profile: any): string | undefined {
  const fromSubscription = profile && profile.subscription && profile.subscription.planId;
  return fromSubscription || (profile && profile.subscriptionPlan && profile.subscriptionPlan.planId) || undefined;
}

/**
 * True when the profile is on the annual plan. The backend interval on the profile's subscription block wins;
 * `isAnnualById` (the plan-id comparison the pages used before) is only the fallback when no interval is sent.
 */
export function isAnnualSubscription(profile: any, isAnnualById: boolean): boolean {
  const interval = profile && profile.subscription && profile.subscription.planInterval;
  if (interval === 'year') {
    return true;
  }
  if (interval === 'month') {
    return false;
  }
  return isAnnualById;
}

/** Backend value when present, else whole days (rounded up) to the expiry date; never negative, null when unknown. */
export function getDaysRemaining(info: SubscriptionInfo | null, now: Date = new Date()): number | null {
  if (!info) {
    return null;
  }
  if (typeof info.daysRemaining === 'number') {
    return Math.max(0, info.daysRemaining);
  }
  const expiry = info.expiryDate ? new Date(info.expiryDate).getTime() : NaN;
  return isNaN(expiry) ? null : Math.max(0, Math.ceil((expiry - now.getTime()) / MS_PER_DAY));
}

/**
 * Reads the subscription block from a profile response. Falls back to the legacy
 * `subscriptionPlan` so the Renew action stays available if the block is missing.
 */
export function toSubscriptionInfo(profile: any): SubscriptionInfo | null {
  if (!profile) {
    return null;
  }
  if (profile.subscription) {
    return profile.subscription as SubscriptionInfo;
  }
  const planId = profile.subscriptionPlan && profile.subscriptionPlan.planId;
  return planId ? { status: 'active', expiryDate: null, planId, canRenew: true } : null;
}

/** A plan from the subscription-plans endpoint; `amount` is in whole currency units. */
export interface PlanPrice {
  id: string;
  amount: number;
  currency: string;
  interval?: string;
}

/** The price of the subscriber's plan, or null when the plan list or plan id is missing or has no match. */
export function findPlanPrice(plans: PlanPrice[] | null, planId: string | null): PlanPrice | null {
  if (!plans || !planId) {
    return null;
  }
  const plan = plans.find((p) => p.id === planId);
  return plan && typeof plan.amount === 'number' ? plan : null;
}
